import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { Timepicker, TimepickerEvents } from "@dhx/ts-timepicker";
import { Popup } from "@dhx/ts-popup";
import { IEditor, SpreadsheetEvents } from "../types";
import { IEventSystem } from "@dhx/ts-common/events";
import { getFormattedValue, isNumeric } from "../helpers/format";
import { excelDateToJSDate, getExcelDateFromJs, timeStringToJS } from "../helpers/date";

export class TimeEditor implements IEditor {
	protected _handlers: { [key: string]: (...args) => void };
	protected _config: any;
	protected _input: HTMLInputElement;
	private _timepicker: Timepicker;
	private _popup: Popup;
	private _events: IEventSystem<SpreadsheetEvents>;
	private _isFromSet: boolean;
	private _initialDate: any;

	constructor(_cb, timeFormat, events) {
		this._timepicker = new Timepicker(null, { timeFormat });
		this._popup = new Popup({ css: "dhx_editor_popup" });
		this._popup.attach(this._timepicker);
		this._events = events;
		this._initHandlers();
	}

	endEdit() {
	}

	setValue(value: number | string) {
		this._isFromSet = true;
		if (this._initialDate && isNumeric(value)) {
			this._initialDate = excelDateToJSDate(value as number);
		}
		let val = isNumeric(value) ?  excelDateToJSDate(value as number) : timeStringToJS(value as string);
		if (val === value) return
		this._timepicker.setValue(val);
	}

	hidePopup() {
		if (this._popup.isVisible()) {
			this._popup.hide();
		}
	}

	toHTML(conf: any) {
		this._config = conf;
		this.setValue(conf.value);
		this._initialDate = conf.initValue;

		document.addEventListener("mousedown", this._handlers.onOuterClick);
		return el("input.dhx_cell_input", {
			...conf,
			onkeydown: e => {
				if (e.code === "Enter") {
					this._popup.hide();
				}
			},
			oninput: this._handlers.oninput,
			_hooks: {
				...conf._hooks,
				didInsert: this._handlers.didInsert,
				didRemove: this._handlers.didRemove,
			},
		});
	}

	protected _showPopup() {
		if (!this._input.value.startsWith("=")) {
			this._popup.show(this._input, {
				centering: true,
				mode: "bottom",
			});
		}
	}

	protected _initHandlers() {
		this._handlers = {
			onFocus: () => {
				awaitRedraw().then(() => {
					this._showPopup();
				});
			},
			onOuterClick: (e: Event) => {
				if (e.target instanceof Node) {
					const isInput = this._input && this._input.contains(e.target);
					const isPopup =
						this._popup &&
						this._popup.getRootNode() &&
						this._popup.getRootNode().contains(e.target);
					if (!(isInput || isPopup)) {
						this._popup.hide();
					}
				}
			},
			oninput: (e: any) => {
				const value = e.target.value;
				if (!value) {
					this._showPopup();
				}
				if (value.startsWith("=") && this._popup.isVisible()) {
					this._popup.hide();
				}
				const [cb, cell] = this._config.oninput;
				this.setValue(value);
				cb(cell, e);
			},
			didInsert: (node: any) => {
				const input: HTMLInputElement = node.el;
				this._input = input;
				this._input.addEventListener("focus", this._handlers.onFocus);
				this._config._hooks.didInsert(node);
				this._initTimePickerEvents();
			},
			didRemove: () => {
				document.removeEventListener("mousedown", this._handlers.onOuterClick);
			},
		};
	}
	protected _initTimePickerEvents() {
		this._timepicker.events.on(TimepickerEvents.change, val => {
			if (this._isFromSet) {
				awaitRedraw().then(() => {
					this._isFromSet = false;
				});
				return;
			}
			this._events.fire(SpreadsheetEvents.cellInput, [
				this._config.oninput[1],
				getFormattedValue(
					getExcelDateFromJs(timeStringToJS(val as string) as Date),
					this._config.format
				),
			]);
		});
	}
}
